import React, { useState } from 'react';
import { RequestState } from '../utils/useRequestState';
import { ENTER_KEY_CODE, shouldTextFieldFocus } from './SignupTextField';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Theme } from '@material-ui/core/styles';
import createStyles from '@material-ui/core/styles/createStyles';
import { textFieldWidth } from './styles';
import OutlinedInput, {
    OutlinedInputProps,
} from '@material-ui/core/OutlinedInput/OutlinedInput';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        searchFieldStyles: {
            display: 'flex',
            width: '100%',
            maxWidth: textFieldWidth,
            margin: '0 auto',
            alignItems: 'center',
            '& .MuiInputBase-input, & .MuiInputBase-input:hover, & .MuiInputBase-input:focus, & .MuiOutlinedInput-root': {
                borderRight: 'none',
                paddingRight: 0,
            },
            '& .button': {
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'center',
                backgroundColor: theme.palette.primary.main,
                width: 58,
                height: 58,
                outline: 'none',
                cursor: 'pointer',
                borderRadius: '0px 6px 6px 0px',
                boxShadow: '0px 0px 6px rgba(118, 142, 168, 0.415237)',
            },
        },
    }),
);

interface SearchTextFieldProps extends Omit<OutlinedInputProps, 'onChange'> {
    name?: string;
    isOptional?: true;
    value: string | undefined;
    placeholder: string;
    type: 'email' | 'number' | 'tel' | 'text' | 'url';
    errorText?: string;
    nullErrorText?: string;
    className?: string;
    isFocus: true | undefined;
    onChange: (value: string, name?: string) => unknown;
    onSubmit?: () => unknown;
    onBlur?: () => unknown;
    onSkip?: () => unknown;
    validation: (value: string | undefined) => boolean;
    maxLength: number;
    requestState?: RequestState<{}>;
    onPressEnter?: () => unknown;
}

export const ariaLabel = { 'aria-label': 'Organization Search' };
const SearchTextField: React.FC<SearchTextFieldProps> = (props) => {
    const {
        name,
        value,
        placeholder,
        type,
        isOptional,
        isFocus,
        errorText,
        nullErrorText,
        className,
        maxLength,
        requestState,
        onBlur,
        onChange,
        onSubmit,
        onSkip,
        validation,
        onPressEnter,
        ...rest
    } = props;
    const { searchFieldStyles } = useStyles();
    const [error, setError] = useState<boolean>(false);
    const onKeyPressFunc = (e: React.KeyboardEvent) => {
        if (e.which === ENTER_KEY_CODE) {
            if (onSubmit && validation(value)) {
                onSubmit();
            } else {
                setError(true);
            }
            if (onPressEnter) {
                onPressEnter();
            }
        }
    };

    const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value, name } = e.target;
        if (value.length > maxLength) return;
        if (error && validation(value)) {
            setError(false);
        }
        onChange(value, name);
    };

    const isValueValid = validation(value);
    const onBlurFunc = () => {
        if (isOptional && !value) return;
        onBlur && onBlur();
        return setError(!isValueValid);
    };

    return (
        <div className={searchFieldStyles}>
            <OutlinedInput
                {...rest}
                autoFocus={shouldTextFieldFocus(isFocus)}
                type={type}
                placeholder={placeholder}
                name={name}
                value={value}
                onChange={onChangeInput}
                onKeyPress={onKeyPressFunc}
                onBlur={onBlurFunc}
                fullWidth={true}
                className={className}
                error={error}
                inputProps={ariaLabel}
            />
        </div>
    );
};

export default SearchTextField;
